import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Plus, Trash2, KeyRound, Users, Shield, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { listStaff, createStaff, updateStaffApi, deleteStaff } from '../../../lib/api';

// Complete list of all sections available in the Admin panel
const SECTION_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'enquiries', label: 'Enquiries & Leads' },
  { key: 'users', label: 'Registered Users' },
  { key: 'chat', label: 'Live Chat' },
  { key: 'reviews', label: 'Reviews & Ratings' },
  { key: 'staff', label: 'Staff & Permissions' },
  { key: 'branding', label: 'Branding & Colors' },
  { key: 'header', label: 'Header Settings' },
  { key: 'navigation', label: 'Top Navigation' },
  { key: 'sidebar', label: 'Control Panel / Sidebar' },
  { key: 'hero', label: 'Hero Slides' },
  { key: 'marquee', label: 'Logo Marquee & Social' },
  { key: 'gamecards', label: 'Service Cards' },
  { key: 'products', label: 'Products (B2C / B2B)' },
  { key: 'exchanges', label: 'Exchange Listings' },
  { key: 'dealingterms', label: 'Dealing Terms' },
  { key: 'payments', label: 'Payments We Accept' },
  { key: 'visibility', label: 'Section Visibility' },
  { key: 'promotions', label: 'Promotions' },
  { key: 'flyerad', label: 'Flyer Ad Popup' },
  { key: 'originals', label: 'Platforms Showcase' },
  { key: 'inplay', label: 'GET ID Platforms' },
  { key: 'modals', label: 'Login / Signup' },
  { key: 'footer', label: 'Footer Settings' },
  { key: 'payment', label: 'Payment Methods' },
  { key: 'aiconfig', label: 'AI Chat Bot' },
  { key: 'botconfig', label: 'OTP Setup' },
  { key: 'botsetting', label: 'Bot Setting' },
  { key: 'settings', label: 'Settings' },
];

export default function StaffSection() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', permissions: [] });
  const [editing, setEditing] = useState(null); // { username, password, permissions }

  const reload = async () => {
    setLoading(true);
    try { setStaff(await listStaff()); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load staff'); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const togglePerm = (list, setList, key) => {
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  };

  const selectAllPerms = (setList) => {
    setList(SECTION_OPTIONS.map((o) => o.key));
  };

  const deselectAllPerms = (setList) => {
    setList([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) {
      toast.error('Username and password required');
      return;
    }
    try {
      await createStaff({ username: form.username.trim(), password: form.password, permissions: form.permissions });
      toast.success('Staff member created successfully');
      setForm({ username: '', password: '', permissions: [] });
      setShowForm(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const body = { permissions: editing.permissions };
      if (editing.password && editing.password.trim()) body.password = editing.password.trim();
      await updateStaffApi(editing.username, body);
      toast.success('Staff permissions updated');
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete staff "${s.username}"?`)) return;
    try { await deleteStaff(s.username); toast.success('Staff deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="staff-section">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-zinc-100 flex items-center gap-2"><Users size={18} /> Staff & Permissions</CardTitle>
          <Button onClick={() => setShowForm((s) => !s)} className="bg-[color:var(--gold)] text-black hover:brightness-110 font-bold" data-testid="add-staff-btn">
            <Plus size={16} className="mr-1" /> {showForm ? 'Close' : 'Add Staff'}
          </Button>
        </CardHeader>
        <CardContent>
          {showForm && (
            <form onSubmit={submit} className="p-4 mb-5 rounded-lg border border-zinc-800 bg-zinc-950 space-y-4" data-testid="staff-create-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs mb-1 block">Username</Label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="staff_username" className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="staff-username-input" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs mb-1 block">Password</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••" className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="staff-password-input" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-zinc-300 text-xs font-semibold">Allowed Admin Sections ({form.permissions.length}/{SECTION_OPTIONS.length})</Label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => selectAllPerms((p) => setForm({ ...form, permissions: p }))} className="text-[11px] text-[color:var(--gold)] hover:underline font-bold flex items-center gap-1">
                      <CheckSquare size={12} /> Select All
                    </button>
                    <span className="text-zinc-600">|</span>
                    <button type="button" onClick={() => deselectAllPerms((p) => setForm({ ...form, permissions: p }))} className="text-[11px] text-zinc-400 hover:text-white font-semibold flex items-center gap-1">
                      <Square size={12} /> Deselect All
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 bg-zinc-900/60 rounded-lg border border-zinc-800">
                  {SECTION_OPTIONS.map((opt) => (
                    <label key={opt.key} className="flex items-center gap-2 text-xs text-zinc-300 p-1.5 rounded hover:bg-zinc-800/80 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={form.permissions.includes(opt.key)}
                        onChange={() => togglePerm(form.permissions, (p) => setForm({ ...form, permissions: p }), opt.key)}
                        className="rounded border-zinc-700 text-[color:var(--gold)] focus:ring-0"
                        data-testid={`staff-perm-${opt.key}`}
                      />
                      <span className="truncate">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" className="bg-[color:var(--gold)] text-black font-bold" data-testid="staff-create-submit-btn">Create Staff Account</Button>
              </div>
            </form>
          )}

          {loading ? <div className="text-zinc-500 text-sm p-4">Loading staff accounts…</div> : staff.length === 0 ? (
            <div className="text-zinc-500 text-sm p-6 text-center">No staff accounts created yet. Click "Add Staff" above to create one.</div>
          ) : (
            <div className="space-y-2">
              {staff.map((s) => (
                <div key={s.username} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3.5 flex items-center justify-between gap-3" data-testid={`staff-row-${s.username}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-[color:var(--gold)] shrink-0" />
                      <span className="text-white font-bold text-sm truncate">{s.username}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400 uppercase font-semibold">{s.role || 'staff'}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-1">
                      {Array.isArray(s.permissions) && s.permissions.length > 0 ? (
                        s.permissions.map((pk) => {
                          const opt = SECTION_OPTIONS.find((o) => o.key === pk);
                          return (
                            <span key={pk} className="px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-300 text-[10px] font-medium border border-zinc-800">
                              {opt?.label || pk}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-zinc-500 italic text-[11px]">No sections assigned (cannot access any tab)</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditing({ username: s.username, password: '', permissions: Array.isArray(s.permissions) ? s.permissions : [] })}
                      className="h-8 px-2.5 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 text-xs font-semibold flex items-center gap-1"
                      data-testid={`staff-edit-${s.username}`}
                    >
                      <KeyRound size={12} /> Edit Permissions
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(s)}
                      className="h-8 px-2.5 rounded-md bg-red-600/15 hover:bg-red-600/25 border border-red-900/40 text-red-300 text-xs font-semibold flex items-center gap-1"
                      data-testid={`staff-delete-${s.username}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4" data-testid="staff-edit-modal">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 max-w-lg w-full space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Shield size={16} className="text-[color:var(--gold)]" /> Edit Staff: {editing.username}
            </h3>
            <div>
              <Label className="text-zinc-300 text-xs mb-1 block">New Password (leave blank to keep current)</Label>
              <Input
                type="password"
                value={editing.password}
                onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                placeholder="••••••"
                className="bg-zinc-900 border-zinc-800 text-zinc-100"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-300 text-xs font-semibold">Allowed Sections ({editing.permissions.length}/{SECTION_OPTIONS.length})</Label>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => selectAllPerms((p) => setEditing({ ...editing, permissions: p }))} className="text-[11px] text-[color:var(--gold)] hover:underline font-bold flex items-center gap-1">
                    <CheckSquare size={12} /> Select All
                  </button>
                  <span className="text-zinc-600">|</span>
                  <button type="button" onClick={() => deselectAllPerms((p) => setEditing({ ...editing, permissions: p }))} className="text-[11px] text-zinc-400 hover:text-white font-semibold flex items-center gap-1">
                    <Square size={12} /> Deselect All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2 bg-zinc-900/60 rounded-lg border border-zinc-800">
                {SECTION_OPTIONS.map((opt) => (
                  <label key={opt.key} className="flex items-center gap-2 text-xs text-zinc-300 p-1.5 rounded hover:bg-zinc-800/80 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={editing.permissions.includes(opt.key)}
                      onChange={() => togglePerm(editing.permissions, (p) => setEditing({ ...editing, permissions: p }), opt.key)}
                      className="rounded border-zinc-700 text-[color:var(--gold)] focus:ring-0"
                    />
                    <span className="truncate">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-900">
              <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-800 text-zinc-300">Cancel</Button>
              <Button onClick={saveEdit} className="bg-[color:var(--gold)] text-black font-bold" data-testid="staff-edit-save-btn">Save Changes</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
